import { createSelector } from 'reselect';
import { isEqual, startCase } from 'lodash/fp';
import { Price } from '@ahmdigital/ui';
import { useSelector } from 'react-redux';
import React from 'react';

import { ProductPriceWrapper } from '../../styles';
import { ProductType } from '../../../../../../types/product';
import { ProductType as ProductTypeType } from '../../../../../../types/__codegen__/ahm-api/graphql';
import { RootState } from '../../../../../../reducers';
import getFutureDate from '../../../../../../utils/get-future-date';
import PriceService from '../../../../../../models/price';
import selectBuildYourCover from '../../../../../../selectors/select-build-your-cover';
import selectCustomer from '../../../../../../selectors/select-customer';
import selectPrices from '../../../../../../selectors/select-prices';
import selectProducts from '../../../../../../selectors/select-products';

const selectStateForComponent = (productType: ProductTypeType, useFutureEffectiveDate: boolean) =>
  createSelector(
    [selectCustomer, selectPrices, selectProducts, selectBuildYourCover],
    (customer, prices, products, { selectedProducts }) => {
      const selectedProductId = selectedProducts[productType];

      if (!selectedProductId || !products?.[selectedProductId]) {
        return { selectedProductName: null, selectedProductPrice: null };
      }

      const date = getFutureDate(useFutureEffectiveDate);

      return {
        // Casting as getting a type error where `!products?.[selectedProductId]` isn't sufficient to enforce type
        selectedProductName: (products[selectedProductId] as ProductType).name,
        selectedProductPrice: parseFloat(
          PriceService.getPrice(prices, selectedProductId, customer, { date })?.amount[customer.frequency] ?? '0',
        ),
      };
    },
  );

type ProductPricePillProps = {
  productType: ProductTypeType;
};

const ProductPricePill = ({ productType }: ProductPricePillProps) => {
  const useFutureEffectiveDate = useSelector((state: RootState) => state.price.useFutureEffectiveDate);

  const { selectedProductName, selectedProductPrice } = useSelector(
    selectStateForComponent(productType, useFutureEffectiveDate),
    isEqual,
  );

  const isSelectedProduct = Boolean(selectedProductName);

  return (
    <ProductPriceWrapper isSelected={isSelectedProduct} data-testid={`product-price-pill-${productType}`}>
      {selectedProductName ? (
        <>
          <span aria-label={`Selected "${selectedProductName}" ${productType} cover`}>{selectedProductName}</span>
          <Price
            price={selectedProductPrice ? PriceService.getPriceAsObject(selectedProductPrice) : null}
            showAsterisk
            variant={['compact', 'inlineSpinner']}
            withoutFrequency
          />
        </>
      ) : (
        <span aria-label={`No ${productType} cover selected`}>{startCase(productType)} cover: —</span>
      )}
    </ProductPriceWrapper>
  );
};

export default ProductPricePill;
